import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  ScreenShare,
  ScreenShareOff,
  SmilePlus,
  Sparkles,
  Users,
  Video,
  VideoOff,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import type { RealtimeCall, RemoteCallParticipant } from '../hooks/useRealtimeCall';
import type { Profile } from '../types';
import Avatar from './Avatar';

function StreamTile({
  name,
  stream,
  muted,
  isLocal,
  avatarUrl,
  videoEnabled,
  audioEnabled,
  screenSharing,
}: {
  name: string;
  stream: MediaStream | null;
  muted?: boolean;
  isLocal?: boolean;
  avatarUrl?: string | null;
  videoEnabled: boolean;
  audioEnabled: boolean;
  screenSharing: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hasVideo = Boolean(videoEnabled && stream?.getVideoTracks().some((track) => track.readyState === 'live'));

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="group relative min-h-[154px] overflow-hidden rounded-2xl border border-white/10 bg-black/30 shadow-soft">
      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={muted}
          className="h-full min-h-[154px] w-full object-cover"
        />
      ) : (
        <div className="grid min-h-[154px] place-items-center bg-[radial-gradient(circle_at_50%_20%,rgba(34,211,238,.14),transparent_42%),linear-gradient(135deg,rgba(15,23,42,.86),rgba(2,6,23,.96))]">
          <Avatar name={name} src={avatarUrl} online size="lg" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/75 to-transparent p-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-white">{isLocal ? `${name} (you)` : name}</p>
          <p className="text-xs text-slate-400">
            {screenSharing ? 'Sharing screen' : hasVideo ? 'Camera on' : 'Voice only'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span
            className={`grid h-7 w-7 place-items-center rounded-lg border ${
              audioEnabled ? 'border-relay-green/25 bg-relay-green/10 text-relay-green' : 'border-relay-red/25 bg-relay-red/10 text-relay-red'
            }`}
            title={audioEnabled ? 'Mic on' : 'Muted'}
          >
            {audioEnabled ? <Mic size={13} /> : <MicOff size={13} />}
          </span>
          <span
            className={`grid h-7 w-7 place-items-center rounded-lg border ${
              hasVideo ? 'border-relay-cyan/25 bg-relay-cyan/10 text-relay-cyan' : 'border-white/10 bg-black/30 text-slate-500'
            }`}
            title={hasVideo ? 'Video on' : 'Video off'}
          >
            {hasVideo ? <Video size={13} /> : <VideoOff size={13} />}
          </span>
        </div>
      </div>
    </div>
  );
}

function ParticipantPill({ participant }: { participant: RemoteCallParticipant }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-2.5 py-2">
      <Avatar name={participant.displayName} src={participant.avatarUrl} online size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-bold text-slate-200">{participant.displayName}</span>
        <span className="text-[11px] text-slate-500">
          {participant.screenSharing ? 'Screen' : participant.videoEnabled ? 'Video' : 'Voice'}
        </span>
      </span>
    </div>
  );
}

export default function CallDock({
  call,
  conversationTitle,
  currentProfile,
}: {
  call: RealtimeCall;
  conversationTitle: string;
  currentProfile: Profile;
}) {
  const showDock = call.active || call.incomingCall || call.error;
  const callTitle = useMemo(() => {
    if (call.incomingCall) return `${call.incomingCall.displayName} is calling`;
    if (call.screenSharing) return 'Screen share live';
    if (call.mode === 'video') return 'Video room live';
    if (call.mode === 'voice') return 'Voice room live';
    return 'Relay call';
  }, [call.incomingCall, call.mode, call.screenSharing]);

  if (!showDock) return null;

  if (call.incomingCall && !call.active) {
    return (
      <section className="mx-3 mt-3 rounded-3xl border border-relay-cyan/20 bg-relay-cyan/10 p-4 shadow-glow">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={call.incomingCall.displayName} src={call.incomingCall.avatarUrl} online size="md" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-white">{call.incomingCall.displayName} started a call</p>
              <p className="truncate text-xs text-slate-400">
                Join the {call.incomingCall.mode} room in {conversationTitle}.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={call.joinIncomingCall}
              className="inline-flex items-center gap-2 rounded-xl bg-relay-cyan px-3 py-2 text-xs font-black text-slate-950 transition hover:bg-cyan-300"
            >
              {call.incomingCall.mode === 'video' ? <Video size={15} /> : <Phone size={15} />}
              Join
            </button>
            <button
              type="button"
              onClick={call.dismissIncomingCall}
              className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 text-slate-400 transition hover:bg-white/10 hover:text-white"
              aria-label="Dismiss incoming call"
            >
              <X size={15} />
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (call.error && !call.active) {
    return (
      <section className="mx-3 mt-3 rounded-3xl border border-relay-red/20 bg-relay-red/10 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-relay-red/15 text-relay-red">
              <PhoneOff size={18} />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-white">Call could not start</p>
              <p className="truncate text-xs text-slate-400">{call.error}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={call.clearError}
            className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label="Dismiss call error"
          >
            <X size={15} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="relative mx-3 mt-3 overflow-hidden rounded-3xl border border-white/10 bg-[#080d17]/95 shadow-2xl shadow-black/25">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-white/[0.035] px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-2xl bg-relay-cyan/10 text-relay-cyan">
            {call.screenSharing ? <ScreenShare size={18} /> : call.mode === 'video' ? <Video size={18} /> : <Phone size={18} />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-black text-white">{callTitle}</p>
              <span className="rounded-full bg-relay-green/10 px-2 py-0.5 text-[11px] font-bold text-relay-green">
                Live
              </span>
            </div>
            <p className="truncate text-xs text-slate-500">{call.error || call.status}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={call.toggleMute}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-xs font-bold transition ${
              call.audioEnabled
                ? 'border border-white/10 bg-white/[0.05] text-slate-200 hover:bg-white/10'
                : 'bg-relay-red/15 text-relay-red hover:bg-relay-red/20'
            }`}
          >
            {call.audioEnabled ? <Mic size={15} /> : <MicOff size={15} />}
            <span className="hidden sm:inline">{call.audioEnabled ? 'Mute' : 'Unmute'}</span>
          </button>
          <button
            type="button"
            onClick={call.toggleCamera}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-xs font-bold transition ${
              call.videoEnabled && !call.screenSharing
                ? 'border border-relay-cyan/25 bg-relay-cyan/10 text-relay-cyan hover:bg-relay-cyan/15'
                : 'border border-white/10 bg-white/[0.05] text-slate-300 hover:bg-white/10'
            }`}
          >
            {call.videoEnabled && !call.screenSharing ? <Video size={15} /> : <VideoOff size={15} />}
            <span className="hidden sm:inline">{call.videoEnabled && !call.screenSharing ? 'Camera' : 'Camera'}</span>
          </button>
          <button
            type="button"
            onClick={call.screenSharing ? call.stopScreenShare : call.startScreenShare}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-xs font-bold transition ${
              call.screenSharing
                ? 'bg-relay-cyan text-slate-950 hover:bg-cyan-300'
                : 'border border-white/10 bg-white/[0.05] text-slate-300 hover:bg-white/10'
            }`}
          >
            {call.screenSharing ? <ScreenShareOff size={15} /> : <ScreenShare size={15} />}
            <span className="hidden sm:inline">{call.screenSharing ? 'Stop share' : 'Share'}</span>
          </button>
          <button
            type="button"
            onClick={call.leaveCall}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-relay-red px-3 text-xs font-black text-white transition hover:bg-red-400"
          >
            <PhoneOff size={15} />
            <span className="hidden sm:inline">Leave</span>
          </button>
        </div>
      </div>

      <div className="grid gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="grid gap-3 md:grid-cols-2">
          <StreamTile
            name={currentProfile.display_name}
            avatarUrl={currentProfile.avatar_url}
            stream={call.localStream}
            muted
            isLocal
            videoEnabled={call.videoEnabled}
            audioEnabled={call.audioEnabled}
            screenSharing={call.screenSharing}
          />
          {call.remoteParticipants.length === 0 && (
            <div className="grid min-h-[154px] place-items-center rounded-2xl border border-dashed border-white/10 bg-white/[0.025] p-5 text-center">
              <div>
                <Sparkles className="mx-auto mb-2 text-relay-cyan" size={20} />
                <p className="text-sm font-bold text-white">Waiting for others</p>
                <p className="mt-1 text-xs text-slate-500">Anyone in this conversation can join from the call banner.</p>
              </div>
            </div>
          )}
          {call.remoteParticipants.map((participant) => (
            <StreamTile
              key={participant.id}
              name={participant.displayName}
              avatarUrl={participant.avatarUrl}
              stream={participant.stream}
              videoEnabled={participant.videoEnabled}
              audioEnabled={participant.audioEnabled}
              screenSharing={participant.screenSharing}
            />
          ))}
        </div>

        <aside className="rounded-2xl border border-white/10 bg-black/20 p-3">
          <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
            <Users size={14} />
            Participants
          </div>
          <div className="space-y-2">
            <ParticipantPill
              participant={{
                id: currentProfile.id,
                username: currentProfile.username,
                displayName: `${currentProfile.display_name} (you)`,
                avatarUrl: currentProfile.avatar_url,
                stream: call.localStream,
                audioEnabled: call.audioEnabled,
                videoEnabled: call.videoEnabled,
                screenSharing: call.screenSharing,
              }}
            />
            {call.remoteParticipants.map((participant) => (
              <ParticipantPill key={participant.id} participant={participant} />
            ))}
          </div>

          <div className="mt-4 border-t border-white/10 pt-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
              <SmilePlus size={14} />
              React
            </div>
            <div className="grid grid-cols-4 gap-2">
              {['👋', '👏', '🔥', '💙'].map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => call.sendReaction(emoji)}
                  className="grid h-9 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-lg transition hover:-translate-y-0.5 hover:bg-white/10"
                  aria-label={`Send ${emoji} reaction`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {call.reactions.length > 0 && (
        <div className="pointer-events-none absolute inset-x-0 top-24 flex justify-center">
          <div className="flex gap-2">
            {call.reactions.map((reaction) => (
              <span
                key={reaction.id}
                className="animate-[floatReaction_3.6s_ease-out_forwards] rounded-full border border-white/10 bg-black/60 px-3 py-1.5 text-sm font-bold text-white shadow-soft backdrop-blur-xl"
              >
                {reaction.emoji} {reaction.displayName}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
